import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  MapPin,
  ChevronDown,
  Check,
  Building2,
  ChevronRight,
  Home,
  School,
  Landmark,
  Compass,
  Users,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useQuery } from "wasp/client/operations";
import { getLocations } from "wasp/client/operations";
import { useNavigate, useParams } from "react-router-dom";
import type { Location } from "wasp/entities";
import { useLocationContext } from "../../contexts/LocationContext";

// Extended Location type with hierarchy fields
interface LocationWithHierarchy extends Location {
  locationType?: { id: string; name: string; slug: string; color: string; iconName: string } | null;
  parentLocation?: { id: string; name: string; locationCode: string } | null;
  childLocations?: {
    id: string;
    name: string;
    locationCode: string;
    isHub?: boolean;
  }[];
  _count?: { childLocations: number };
}

interface LocationDropdownProps {
  className?: string;
}

export default function LocationDropdown({ className }: LocationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"res" | "academic" | "campus" | "all">("all");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();

  // Use location context
  const { selectedLocation, setSelectedLocation, locations, isLoading } =
    useLocationContext();

  // Check if we're on an admin page or any of the main dashboard pages
  const isAdminPage = window.location.pathname.includes("/admin/");
  const isDashboardPage = window.location.pathname === "/dashboard";
  const isNoticesPage = window.location.pathname.includes("/notices");
  const isEventsPage = window.location.pathname.includes("/events");
  const isTenantsPage = window.location.pathname.includes("/tenants");
  const isFacilitiesPage = window.location.pathname.includes("/facilities");
  const isBuySellPage = window.location.pathname.includes("/buy-sell");
  const isFoodCourtPage = window.location.pathname.includes("/food-court");
  const isLostFoundPage = window.location.pathname.includes("/lost-found");
  const isRetailPage = window.location.pathname.includes("/retail");
  const isServicesPage = window.location.pathname.includes("/services");
  const isRequestOfferPage =
    window.location.pathname.includes("/request-offer");
  const isImmediateHelpPage = window.location.pathname.includes("/immediate-help");
  const isAskForHelpPage = window.location.pathname.includes("/ask-for-help");
  const isShareWithNeighborsPage = window.location.pathname.includes(
    "/share-with-neighbors",
  );
  const isOtherRequestsPage =
    window.location.pathname.includes("/other-requests");
  const isAccommodationPage = window.location.pathname.includes("/accommodation");
  const isCarpoolingPage = window.location.pathname.includes("/carpooling");
  const shouldUseCleanStyle =
    isAdminPage ||
    isDashboardPage ||
    isNoticesPage ||
    isEventsPage ||
    isTenantsPage ||
    isFacilitiesPage ||
    isBuySellPage ||
    isFoodCourtPage ||
    isLostFoundPage ||
    isRetailPage ||
    isServicesPage ||
    isRequestOfferPage ||
    isImmediateHelpPage ||
    isAskForHelpPage ||
    isShareWithNeighborsPage ||
    isOtherRequestsPage ||
    isAccommodationPage ||
    isCarpoolingPage;

  // Build hierarchical location list - must run unconditionally (before any early return) to satisfy Rules of Hooks
  const hierarchicalLocations = useMemo(() => {
    if (!locations || !Array.isArray(locations) || locations.length === 0)
      return [];
    const typedLocations = locations as LocationWithHierarchy[];
    const rootLocations = typedLocations.filter((l) => !l.parentLocationId);
    const buildTree = (
      parentId: string | null,
      depth: number,
    ): { location: LocationWithHierarchy; depth: number }[] => {
      const children = typedLocations.filter(
        (l) => l.parentLocationId === parentId,
      );
      const result: { location: LocationWithHierarchy; depth: number }[] = [];
      for (const child of children) {
        result.push({ location: child, depth });
        result.push(...buildTree(child.id, depth + 1));
      }
      return result;
    };
    const tree: { location: LocationWithHierarchy; depth: number }[] = [];
    for (const root of rootLocations) {
      tree.push({ location: root, depth: 0 });
      tree.push(...buildTree(root.id, 1));
    }
    return tree;
  }, [locations]);

  // Set initial selected location based on URL params or first available location
  useEffect(() => {
    if (
      locations &&
      Array.isArray(locations) &&
      locations.length > 0 &&
      !selectedLocation
    ) {
      if (params.slug) {
        // Find location by slug (locationCode)
        const locationBySlug = locations.find(
          (loc) => loc.locationCode === params.slug,
        );
        if (locationBySlug) {
          setSelectedLocation(locationBySlug);
        } else {
          // Fallback to first location if slug not found
          setSelectedLocation(locations[0]);
        }
      } else {
        // No slug in URL, use first available location
        setSelectedLocation(locations[0]);
      }
    }
  }, [locations, params.slug, selectedLocation, setSelectedLocation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    setIsOpen(false);

    // Navigate to the selected location's appropriate page based on current context
    const currentPath = window.location.pathname;

    if (location.locationCode) {
      // Check for admin pages first
      if (currentPath.includes("/admin/permissions")) {
        // Stay on admin permissions page - just update location context
        return;
      } else if (currentPath.includes("/admin/recycle-bin")) {
        // Stay on admin recycle bin page - just update location context
        return;
      } else if (currentPath.includes("/admin/")) {
        // For other admin pages, stay on the same admin page
        return;
      }
      // Check for dashboard page - stay on /dashboard and just update location context
      else if (currentPath === "/dashboard") {
        // Stay on /dashboard - just update location context
        return;
      }
      // Check for specific pages in order of specificity
      else if (currentPath.includes("/notices/add")) {
        navigate(`/locations/${location.locationCode}/notices/add`);
      } else if (
        currentPath.includes("/notices/") &&
        currentPath.includes("/edit")
      ) {
        // For edit pages, extract notice ID and navigate to location-specific edit
        const noticeId = currentPath.split("/notices/")[1]?.split("/edit")[0];
        if (noticeId) {
          navigate(
            `/locations/${location.locationCode}/notices/${noticeId}/edit`,
          );
        } else {
          navigate(`/locations/${location.locationCode}/notices`);
        }
      } else if (
        currentPath.includes("/notices/") &&
        !currentPath.includes("/add") &&
        !currentPath.includes("/edit")
      ) {
        // For view pages, extract notice ID and navigate to location-specific view
        const noticeId = currentPath.split("/notices/")[1];
        if (noticeId) {
          navigate(`/locations/${location.locationCode}/notices/${noticeId}`);
        } else {
          navigate(`/locations/${location.locationCode}/notices`);
        }
      } else if (currentPath.includes("/notices")) {
        navigate(`/locations/${location.locationCode}/notices`);
      } else if (currentPath.includes("/tenants")) {
        navigate(`/locations/${location.locationCode}/tenants`);
      } else if (currentPath.includes("/events")) {
        navigate(`/locations/${location.locationCode}/events`);
      } else if (currentPath.includes("/facilities")) {
        navigate(`/locations/${location.locationCode}/facilities`);
      } else if (currentPath.includes("/buy-sell")) {
        navigate(`/locations/${location.locationCode}/buy-sell`);
      } else if (currentPath.includes("/food-court")) {
        navigate(`/locations/${location.locationCode}/food-court`);
      } else if (currentPath.includes("/lost-found")) {
        navigate(`/locations/${location.locationCode}/lost-found`);
      } else if (currentPath.includes("/retail")) {
        navigate(`/locations/${location.locationCode}/retail`);
      } else if (currentPath.includes("/services")) {
        navigate(`/locations/${location.locationCode}/services`);
      } else if (currentPath.includes("/request-offer")) {
        navigate(`/locations/${location.locationCode}/request-offer`);
      } else if (currentPath.includes("/immediate-help")) {
        navigate(`/locations/${location.locationCode}/immediate-help`);
      } else if (currentPath.includes("/ask-for-help")) {
        navigate(`/locations/${location.locationCode}/ask-for-help`);
      } else if (currentPath.includes("/share-with-neighbors")) {
        navigate(`/locations/${location.locationCode}/share-with-neighbors`);
      } else if (currentPath.includes("/other-requests")) {
        navigate(`/locations/${location.locationCode}/other-requests`);
      } else if (currentPath.includes("/accommodation")) {
        navigate(`/locations/${location.locationCode}/accommodation`);
      } else if (currentPath.includes("/carpooling")) {
        navigate(`/locations/${location.locationCode}/carpooling`);
      } else {
        // For any other page, navigate to location-specific dashboard
        navigate(`/locations/${location.locationCode}/dashboard`);
      }
    } else {
      // Fallback for locations without codes
      if (currentPath.includes("/admin/")) {
        // For admin pages, stay on the same page
        return;
      } else if (currentPath.includes("/tenants")) {
        navigate("/tenants");
      } else if (currentPath.includes("/notices")) {
        navigate("/notices");
      } else if (currentPath.includes("/events")) {
        navigate("/events");
      } else if (currentPath.includes("/facilities")) {
        navigate("/facilities");
      } else if (currentPath.includes("/buy-sell")) {
        navigate("/buy-sell");
      } else if (currentPath.includes("/food-court")) {
        navigate("/food-court");
      } else if (currentPath.includes("/lost-found")) {
        navigate("/lost-found");
      } else if (currentPath.includes("/retail")) {
        navigate("/retail");
      } else if (currentPath.includes("/services")) {
        navigate("/services");
      } else if (currentPath.includes("/request-offer")) {
        navigate("/request-offer");
      } else if (currentPath.includes("/immediate-help")) {
        navigate("/immediate-help");
      } else if (currentPath.includes("/ask-for-help")) {
        navigate("/ask-for-help");
      } else if (currentPath.includes("/share-with-neighbors")) {
        navigate("/share-with-neighbors");
      } else if (currentPath.includes("/other-requests")) {
        navigate("/other-requests");
      } else if (currentPath.includes("/accommodation")) {
        navigate("/accommodation");
      } else if (currentPath.includes("/carpooling")) {
        navigate("/carpooling");
      } else {
        navigate("/dashboard");
      }
    }
  };

  const renderLocationNode = (
    node: LocationWithHierarchy,
    allLocations: LocationWithHierarchy[],
    depth: number,
    selectedId?: string,
  ) => {
    const children = allLocations.filter((l) => l.parentLocationId === node.id);
    const isSelected = selectedId === node.id;
    const typedNode = node as LocationWithHierarchy;

    if (depth === 0) {
      return (
        <div
          key={node.id}
          className="flex flex-col break-inside-avoid shadow-sm bg-card mb-3 rounded-lg border border-border/50 overflow-hidden hover:border-border transition-colors"
        >
          <button
            onClick={() => handleLocationSelect(node)}
            className={cn(
              "text-left font-bold text-base text-content-text hover:text-primary transition-colors flex items-center justify-between group p-2 bg-muted/30 w-full",
              isSelected && "text-primary bg-primary/5",
            )}
          >
            <div className="flex items-center">
              <span className="whitespace-nowrap">{node.name}</span>
            </div>
            {isSelected && (
              <Check className="w-5 h-5 text-primary flex-shrink-0" />
            )}
          </button>
          {children.length > 0 && (
            <div className="p-1 space-y-0.5 bg-card">
              {children.map((child) =>
                renderLocationNode(child, allLocations, depth + 1, selectedId),
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div key={node.id} className="flex flex-col w-full">
          <button
            onClick={() => handleLocationSelect(node)}
            className={cn(
              "text-left py-1 px-2 text-sm text-content-text-muted hover:text-content-text hover:bg-sidebar-hover rounded-md transition-colors flex items-center justify-between w-full group",
              isSelected && "text-primary font-medium bg-sidebar-active",
            )}
            style={{ paddingLeft: `${(depth - 1) * 12 + 12}px` }}
          >
            <div className="flex items-center">
              <span className="pr-2 whitespace-nowrap">{node.name}</span>
            </div>
            {isSelected && (
              <Check className="w-4 h-4 text-primary flex-shrink-0" />
            )}
          </button>
          {children.length > 0 && (
            <div className="mt-0.5 space-y-0.5">
              {children.map((child) =>
                renderLocationNode(child, allLocations, depth + 1, selectedId),
              )}
            </div>
          )}
        </div>
      );
    }
  };

  const renderDropdownContent = () => {
    const rootLocations = (locations as LocationWithHierarchy[]).filter(
      (l) => !l.parentLocationId,
    );
    const mainCampus = rootLocations.find(
      (l) =>
        l.locationCode === "UNI-MAIN-001" || l.name.includes("Main Campus"),
    );
    const rest = rootLocations.filter((l) => l !== mainCampus);
    const res = rest.filter((l) => l.locationType?.slug === "RESIDENCE" || l.locationType?.slug === "DORM");
    const academic = rest.filter((l) => l.locationType?.slug === "FACULTY");
    const campusLife = rest.filter((l) =>
      ["STUDENT_CENTER", "LIBRARY", "SPORTS_COMPLEX", "FOOD_COURT"].includes(l.locationType?.slug || "")
    );
    const admin = rest.filter((l) =>
      ["ADMIN_OFFICE", "CAMPUS"].includes(l.locationType?.slug || "")
    );

    const tabs = [
      { id: "all", label: "All Items", icon: Compass },
      { id: "res", label: "Residences", icon: Home, count: res.length },
      { id: "academic", label: "Academic", icon: School, count: academic.length },
      { id: "campus", label: "Campus Life", icon: Users, count: campusLife.length },
    ];

    return (
      <div className="flex flex-col gap-6 w-full">
        {/* Tab Header */}
        <div className="flex items-center gap-1 border-b border-border p-1 bg-muted/30 rounded-t-lg -mx-6 -mt-6 mb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all rounded-md whitespace-nowrap",
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-content-text-muted hover:bg-hover hover:text-content-text"
              )}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={cn(
                  "ml-1 text-[10px] px-1.5 py-0.5 rounded-full",
                  activeTab === tab.id ? "bg-primary-foreground/20" : "bg-muted"
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-6">
          {activeTab === "all" && mainCampus && (
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3">
                <Compass className="w-4 h-4 text-primary" />
                <h3 className="text-xs font-bold text-content-text uppercase tracking-wider">Main Hub</h3>
              </div>
              {renderLocationNode(
                mainCampus,
                locations as LocationWithHierarchy[],
                0,
                selectedLocation?.id,
              )}
            </div>
          )}

          {(activeTab === "all" || activeTab === "res") && res.length > 0 && (
            <div className="w-full border-t border-border/50 pt-4 first:border-t-0 first:pt-0">
              <div className="flex items-center gap-2 mb-4">
                <Home className="w-4 h-4 text-primary" />
                <h3 className="text-xs font-bold text-content-text uppercase tracking-wider">
                  Our Residences
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {res.map((root) =>
                  renderLocationNode(
                    root,
                    locations as LocationWithHierarchy[],
                    0,
                    selectedLocation?.id,
                  ),
                )}
              </div>
            </div>
          )}

          {(activeTab === "all" || activeTab === "academic") && academic.length > 0 && (
            <div className="w-full border-t border-border/50 pt-4">
              <div className="flex items-center gap-2 mb-4">
                <School className="w-4 h-4 text-primary" />
                <h3 className="text-xs font-bold text-content-text uppercase tracking-wider">
                  Academic Buildings
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {academic.map((root) =>
                  renderLocationNode(
                    root,
                    locations as LocationWithHierarchy[],
                    0,
                    selectedLocation?.id,
                  ),
                )}
              </div>
            </div>
          )}

          {(activeTab === "all" || activeTab === "campus") && campusLife.length > 0 && (
            <div className="w-full border-t border-border/50 pt-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-primary" />
                <h3 className="text-xs font-bold text-content-text uppercase tracking-wider">
                  Campus Life & Services
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {campusLife.map((root) =>
                  renderLocationNode(
                    root,
                    locations as LocationWithHierarchy[],
                    0,
                    selectedLocation?.id,
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // For admin pages and main dashboard pages, show clean dropdown without icons and borders
  if (shouldUseCleanStyle) {
    if (isLoading) {
      return (
        <div
          className={cn(
            "text-base font-medium text-content-text-muted",
            className,
          )}
        >
          Loading...
        </div>
      );
    }

    if (!locations || !Array.isArray(locations) || locations.length === 0) {
      return (
        <div
          className={cn(
            "text-base font-medium text-content-text-muted",
            className,
          )}
        >
          No locations
        </div>
      );
    }

    const typedSelectedLocation =
      selectedLocation as LocationWithHierarchy | null;

    return (
      <div className={cn("relative min-w-0", className)} ref={dropdownRef}>
        {/* Clean Location Selector Button for Admin Pages */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-full min-w-0 flex items-center gap-2 text-base font-semibold text-content-text hover:text-content-text-muted",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded px-2 py-1 text-left",
            "transition-colors duration-200",
          )}
        >
          {/* No icon for hub in top bar */}
          <span className="truncate">{typedSelectedLocation?.name || "Select Location"}</span>
        </button>

        {/* Clean Dropdown Menu for Admin Pages */}
        {isOpen && locations && Array.isArray(locations) && (
          <div className="absolute top-full left-0 mt-2 w-[90vw] lg:w-[900px] xl:w-[1200px] bg-card border border-border rounded-xl shadow-2xl z-[9999] p-6 max-h-[85vh] overflow-x-auto overflow-y-auto">
            {renderDropdownContent()}
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center px-3 py-1.5 border border-input-border rounded-lg",
          className,
        )}
      >
        <span className="text-base font-medium text-content-text-muted">
          Loading...
        </span>
      </div>
    );
  }

  if (!locations || !Array.isArray(locations) || locations.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center px-3 py-1.5 border border-input-border rounded-lg",
          className,
        )}
      >
        <span className="text-base font-medium text-content-text-muted">
          No locations
        </span>
      </div>
    );
  }

  const typedSelectedLocation =
    selectedLocation as LocationWithHierarchy | null;

  return (
    <div className={cn("relative min-w-0", className)} ref={dropdownRef}>
      {/* Location Selector Button - full width so parent fixed width is used, text truncates */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full min-w-0 flex items-center px-3 py-1.5 border border-input-border rounded-lg",
          "hover:border-button-border transition-colors cursor-pointer text-left",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
        )}
      >
        <span className="text-base font-medium text-content-text truncate">
          {selectedLocation?.name || "Select Location"}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && locations && Array.isArray(locations) && (
        <div className="absolute top-full left-0 mt-2 w-[90vw] lg:w-[900px] xl:w-[1200px] bg-card border border-border rounded-xl shadow-2xl z-[60] p-6 max-h-[85vh] overflow-x-auto overflow-y-auto">
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
}
